import { parseCalendarDate } from "../../utils/date";
import { matchZone, venueDay, matchVenueDate, matchHasEnded } from "./matchTime";
import { hasRegistrationPolicy, registrationGate } from "./registrationPolicy";

export const DISCIPLINES = { nra_arc: "NRA ARC", bullseye: "Bullseye" };
export const matchKey = (match) => `${match.competitionType}:${match.matchId}`;

export function normalizeMatch(match, competitionType) {
  return {
    ...match,
    competitionType,
    detailRoute: `/matches/${competitionType === "bullseye" ? "bullseye" : "arc"}/${encodeURIComponent(match.matchId)}`,
  };
}

function calendarDay(value, zone = "UTC") {
  const day = venueDay(value, zone);
  return day ? Date.parse(`${day}T00:00:00Z`) : null;
}

// Discovery follows the event's calendar dates, even if a device has not updated its status.
export function matchPhase(match, now = new Date()) {
  const status = String(match.status || "scheduled").toLowerCase();
  if (match.lifecycle?.cancelledAt || ["cancelled", "canceled"].includes(status)) return "cancelled";
  if (["completed", "final", "official", "closed"].includes(status)) return "completed";
  const zone = matchZone(match);
  const today = calendarDay(now, zone);
  const start = calendarDay(match.startDate || match.matchDate, zone);
  const end = calendarDay(match.endDate, zone) ?? start;
  if (matchHasEnded(match, now)) return "completed";
  if (status === "in_progress" || (start !== null && start < today && end >= today)) return "in_progress";
  return "scheduled";
}

export function matchLocation(match) {
  return String(match.location || [match.city, match.state].filter(Boolean).join(", ") || match.rangeName || "Location to be announced").trim();
}

export function matchDateLabel(match) {
  const start = matchVenueDate(match);
  const end = matchVenueDate(match, match.endDate);
  if (!start) return "Date to be announced";
  const options = { month: "short", day: "numeric", year: "numeric" };
  const label = start.toLocaleDateString(undefined, options);
  return end && end.toDateString() !== start.toDateString()
    ? `${label} – ${end.toLocaleDateString(undefined, options)}` : label;
}

export function registrationLabel(match, now = new Date()) {
  if (hasRegistrationPolicy(match)) {
    const gate = registrationGate(match, match, now);
    const labels = { open: "Registration open", full: "Match full", not_open: "Registration not open yet", scheduled: "Registration not open yet", paused: "Registration paused", closed: "Registration closed", started: "In progress", completed: "Past match", cancelled: "Cancelled", program_incomplete: "Registration opens soon" };
    return { label: labels[gate.state] || "View registration details", tone: gate.state === "open" || gate.state === "started" ? "open" : "muted" };
  }
  const phase = matchPhase(match, now);
  if (phase === "completed") return { label: "Past match", tone: "muted" };
  if (phase === "cancelled") return { label: "Cancelled", tone: "muted" };
  if (phase === "in_progress") return { label: "In progress", tone: "open" };
  if (match.registrationOpen === true) return { label: "Registration open", tone: "open" };
  if (match.registrationOpen === false) return { label: "Registration closed", tone: "muted" };
  return { label: "View registration details", tone: "muted" };
}

export function withinBounds(coordinates, bounds) {
  if (!coordinates || !bounds) return !bounds;
  const { latitude, longitude } = coordinates;
  const normalizeLng = (value) => ((value + 180) % 360 + 360) % 360 - 180;
  const west = normalizeLng(bounds.west);
  const east = normalizeLng(bounds.east);
  const inLongitude = bounds.east - bounds.west >= 360 || (west <= east
    ? longitude >= west && longitude <= east : longitude >= west || longitude <= east);
  return latitude >= bounds.south && latitude <= bounds.north && inLongitude;
}

export function filterMatches(matches, { status = "scheduled", family = "all", query = "", location = "", date = "any" }, now = new Date()) {
  const text = query.trim().toLowerCase();
  return matches.filter((match) => {
    const phase = matchPhase(match, now);
    if (status !== "all" && (status === "scheduled" ? !["scheduled", "in_progress"].includes(phase) : phase !== status)) return false;
    if (family !== "all" && family !== match.competitionType) return false;
    if (location && String(match.state || "").trim() !== location) return false;
    if (text && ![match.matchName, match.location, match.city, match.state, match.rangeName, DISCIPLINES[match.competitionType]]
      .some((value) => String(value || "").toLowerCase().includes(text))) return false;
    if (date !== "any") {
      const zone = matchZone(match), today = calendarDay(now, zone), venueNow = new Date(today);
      const start = calendarDay(match.startDate || match.matchDate, zone);
      const end = calendarDay(match.endDate, zone) ?? start;
      const until = date === "month" ? Date.UTC(venueNow.getUTCFullYear(), venueNow.getUTCMonth() + 1, 0) : today + 6 * 86400000;
      if (start === null || end < today || start > until) return false;
    }
    return true;
  });
}

export function sortMatches(matches, sort) {
  return [...matches].sort((a, b) => {
    const left = parseCalendarDate(a.startDate || a.matchDate)?.getTime();
    const right = parseCalendarDate(b.startDate || b.matchDate)?.getTime();
    if (left === undefined && right !== undefined) return 1;
    if (right === undefined && left !== undefined) return -1;
    return ((left ?? 0) - (right ?? 0)) * (sort === "latest" ? -1 : 1)
      || String(a.matchName || "").localeCompare(String(b.matchName || ""));
  });
}
